// libraries
import React, { useMemo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { Stack, Typography, Box, Button, IconButton } from '@mui/material'
import { AddCircle, Delete } from '@mui/icons-material'
import { makeStyles } from '@mui/styles'
import { randomId } from '@mui/x-data-grid-generator'

// generic components
import BasicDataGrid from 'components/generic/datagrid'
import EditableCell from 'components/generic/datagrid/cells/EditableCell'
import DatePicker from 'components/generic/form/DatePicker'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'

// helpers and utils
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { setDecimalPrecision } from 'utils/helpers'
import { MEASURE_UNIT, MEASURE_UNIT_ARRAY, PAGE_SIZE } from 'utils/constants'
import { addToDate } from 'utils/data-manipulation/common'

const useStyles = makeStyles(theme => ({
  root: {
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(1),
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      padding: '14px',
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(0),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    // replace default hover / active-row styling from datagrid
    '& .MuiDataGrid-row': {
      '&.Mui-selected': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
      '&:not(.Mui-selected)': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
}))

const FreightLaneBladePricing = ({ freightLanePricesData, apiRef, isUpdate, setHasEditedPrices }) => {
  const today = useMemo(() => new Date(), [])
  const classes = useStyles()
  const methods = useFormContext()
  const { control, setValue, getValues, formState } = methods
  const { errors } = formState

  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  const { settings, defaultYardUm } = useSelector(state => state.settings)
  const { disableUmDropdowns } = settings?.purchaseSettings?.inboundTickets || {}

  const onClickNewPricing = useCallback(() => {
    setHasEditedPrices(true)
    const currentGridRowsData = Array.from(apiRef.current.getRowModels().values())
      ?.map(row => {
        const rowFormData = getValues(`pricing.${row.id}`)
        return {
          ...row,
          effectiveDate: Object.prototype.hasOwnProperty.call(rowFormData || {}, 'effectiveDate')
            ? rowFormData?.effectiveDate || null
            : row?.effectiveDate,
          expirationDate: rowFormData?.expirationDate || null,
        }
      })
      ?.sort((a, b) => {
        if (!a.expirationDate) return 1
        if (!b.expirationDate) return -1
        return new Date(a.expirationDate) - new Date(b.expirationDate)
      })

    const previousRow = currentGridRowsData[currentGridRowsData.length - 1]
    const newRow = {
      ...previousRow,
      id: randomId(),
      isNew: true,
      effectiveDate: null,
      expirationDate: null,
    }

    if (previousRow?.expirationDate) {
      const newEffectiveDate = addToDate(previousRow.expirationDate)
      newRow.effectiveDate = Number.isNaN(newEffectiveDate.getTime()) ? null : newEffectiveDate.toISOString()
    } else if (previousRow?.effectiveDate && new Date(previousRow.effectiveDate) <= today) {
      setValue(`pricing.${previousRow.id}.expirationDate`, today)
      const newEffectiveDate = addToDate(today)
      newRow.effectiveDate = Number.isNaN(newEffectiveDate.getTime()) ? null : newEffectiveDate.toISOString()
    } else if (!previousRow) {
      newRow.effectiveDate = today.toISOString()
    }

    apiRef.current.updateRows([newRow])
    setTimeout(() => {
      apiRef.current.scrollToIndexes({
        rowIndex: apiRef.current.getRowsCount() - 1,
      })
    }, 150)
  }, [apiRef])

  const rows = useMemo(() => {
    if (isUpdate) {
      return (
        freightLanePricesData?.Items?.filter(
          price => !price.expirationDate || new Date(price.expirationDate) > today,
        )?.sort((a, b) => {
          if (!a.expirationDate) return 1
          if (!b.expirationDate) return -1
          return new Date(a.expirationDate) - new Date(b.expirationDate)
        }) || []
      )
    }
    return [{ id: randomId(), isNew: true, effectiveDate: today.toISOString(), expirationDate: null }]
  }, [isUpdate, freightLanePricesData])

  const pricingColumns = useMemo(() => {
    const columns = [
      {
        field: 'effectiveDate',
        headerName: 'Effective Date',
        flex: 3,
        sortable: false,
        renderCell: params => {
          const { row = {} } = params
          const { id, effectiveDate, isNew } = row
          const dateTimeObj = effectiveDate ? new Date(effectiveDate) : null
          if (!isNew && dateTimeObj && dateTimeObj < today) {
            return (
              <Typography pl="14px" variant="body2">
                {dateFormatter(effectiveDate)}
              </Typography>
            )
          }

          return (
            <DatePicker
              name={`pricing.${id}.effectiveDate`}
              label=""
              variant="outlined"
              size="small"
              control={control}
              error={!!errors?.pricing?.[id]?.effectiveDate}
              defaultDate={dateTimeObj}
              datePickerProps={{
                disablePast: true,
              }}
              onFocus={event => setTimeout(() => event.target.focus(), 100)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                sx: {
                  fontSize: '0.875rem',
                  textAlign: 'right',
                },
              }}
            />
          )
        },
      },
      {
        field: 'expirationDate',
        headerName: 'Expiration Date',
        flex: 3,
        sortable: false,
        renderCell: params => {
          const { row = {} } = params
          const { id, expirationDate } = row
          const effectiveDate = getValues(`pricing.${id}.effectiveDate`)
          const effectiveDateTimeObj = effectiveDate ? new Date(effectiveDate) : null
          const expirationDateTimeObj = expirationDate ? new Date(expirationDate) : null

          return (
            <DatePicker
              name={`pricing.${id}.expirationDate`}
              label=""
              variant="outlined"
              size="small"
              control={control}
              error={!!errors?.pricing?.[id]?.expirationDate}
              defaultDate={expirationDateTimeObj}
              datePickerProps={{
                disablePast: true,
                minDate: effectiveDateTimeObj,
                defaultCalendarMonth: Math.max(effectiveDateTimeObj, today),
              }}
              onFocus={event => setTimeout(() => event.target.focus(), 100)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                sx: {
                  fontSize: '0.875rem',
                  textAlign: 'right',
                },
              }}
            />
          )
        },
      },
      {
        field: 'minCost',
        headerName: 'Min Price',
        flex: 2,
        sortable: false,
        headerAlign: 'right',
        align: 'right',
        valueGetter: params => {
          const { minCost = 0 } = params.row
          return `$${setDecimalPrecision(minCost, { decimalPrecisionOverride: 2 })}`
        },
        renderCell: params => {
          const { row = {} } = params
          const { id, minCost, effectiveDate, isNew } = row
          if (!isNew && effectiveDate && new Date(effectiveDate) < today) {
            return (
              <Typography pr="14px" variant="body2">
                ${setDecimalPrecision(minCost, { decimalPrecisionOverride: 2 })}
              </Typography>
            )
          }

          return (
            <EditableCell
              control={control}
              name={`pricing.${id}.minCost`}
              defaultValue={minCost || minCost === 0 ? setDecimalPrecision(minCost) : ''}
              isEdit
              size="small"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  inputType: 'price',
                  decimalScale: 2,
                  allowNegative: false,
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                  onChange: event => {
                    const newValue = event?.target?.value
                    if (newValue?.toString() === '.') return
                    apiRef.current.updateRows([{ ...row, minCost: newValue }])
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
      },
      {
        field: 'costPerUm',
        headerName: 'Price/UM',
        flex: 2,
        sortable: false,
        headerAlign: 'right',
        align: 'right',
        renderCell: params => {
          const { row = {} } = params
          const { id, costPerUm, effectiveDate, isNew } = row
          if (!isNew && effectiveDate && new Date(effectiveDate) < today) {
            return (
              <Typography pr="14px" variant="body2">
                ${setDecimalPrecision(costPerUm, { decimalPrecisionOverride: 2 })}
              </Typography>
            )
          }

          return (
            <EditableCell
              control={control}
              name={`pricing.${id}.costPerUm`}
              defaultValue={costPerUm || costPerUm === 0 ? setDecimalPrecision(costPerUm) : ''}
              isEdit
              size="small"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  inputType: 'price',
                  decimalScale: 2,
                  allowNegative: false,
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                  onChange: event => {
                    const newValue = event?.target?.value
                    if (newValue?.toString() === '.') return
                    apiRef.current.updateRows([{ ...row, costPerUm: newValue }])
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
      },
      {
        field: 'um',
        headerName: 'UM',
        flex: 2,
        sortable: false,
        renderCell: params => {
          const { row } = params
          const { id, um, effectiveDate, isNew } = row
          if (!isNew && effectiveDate && new Date(effectiveDate) < today) {
            return (
              <Typography pl="14px" variant="body2">
                {um?.toUpperCase()}
              </Typography>
            )
          }

          return (
            <EditableCell
              name={`pricing.${id}.um`}
              control={control}
              label=""
              defaultValue={(um || defaultYardUm).toLowerCase()}
              cellType="SELECT"
              size="small"
              options={MEASURE_UNIT_ARRAY}
              isEdit
              onChange={event => {
                const { value } = event.target
                apiRef.current?.updateRows([{ ...row, um: value }])
              }}
              InputProps={{
                sx: { fontSize: '0.875rem' },
                inputProps: {
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                },
              }}
            />
          )
        },
      },
      {
        field: 'fuelSurcharge',
        headerName: 'Fuel Surcharge',
        flex: 2,
        sortable: false,
        headerAlign: 'right',
        align: 'right',
        renderCell: params => {
          const { row = {} } = params
          const { id, fuelSurcharge, effectiveDate, isNew } = row
          if (!isNew && effectiveDate && new Date(effectiveDate) < today) {
            return (
              <Typography pr="14px" variant="body2">
                {setDecimalPrecision(fuelSurcharge, { decimalPrecisionOverride: 1 })}%
              </Typography>
            )
          }

          return (
            <EditableCell
              control={control}
              name={`pricing.${id}.fuelSurcharge`}
              defaultValue={fuelSurcharge || fuelSurcharge === 0 ? setDecimalPrecision(fuelSurcharge) : ''}
              isEdit
              size="small"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  inputType: 'percent',
                  decimalScale: 1,
                  allowNegative: false,
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                  onChange: event => {
                    const newValue = event?.target?.value
                    if (newValue?.toString() === '.') return
                    apiRef.current.updateRows([{ ...row, fuelSurcharge: newValue }])
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
      },
      {
        field: 'actions',
        headerName: '',
        renderCell: params => {
          const { row = {} } = params
          const { id, isNew } = row

          return (
            <IconButton
              color="error"
              size="small"
              sx={{
                fill: 'red',
                width: '40px',
                height: '40px',
              }}
              aria-label="delete"
              disabled={!isNew}
              onClick={event => {
                event.stopPropagation()
                apiRef.current.updateRows([{ id, _action: 'delete' }])
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          )
        },
        headerAlign: 'center',
        align: 'center',
        sortable: false,
      },
    ]
    const filteredColumns = disableUmDropdowns ? columns.filter(column => column.field !== 'um') : columns
    return filteredColumns
  }, [apiRef, errors, disableUmDropdowns, defaultYardUm])

  return (
    <Stack mt={5}>
      <Typography variant="h6" gutterBottom>
        Active and Upcoming Pricing
      </Typography>
      <Box py={1}>
        <Button startIcon={<AddCircle />} onClick={onClickNewPricing} data-testid="active-pricing-new-button">
          Add Pricing
        </Button>
      </Box>
      <BasicDataGrid
        className={classes.root}
        pageSize={pageSize}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        rowsPerPageOptions={[10, 20, 30]}
        pagination
        rows={rows}
        columns={pricingColumns}
        apiRef={apiRef}
        disableColumnMenu
        hideFooterSelectedRowCount
        onCellClick={(_, event) => event.stopPropagation()}
      />
    </Stack>
  )
}

FreightLaneBladePricing.propTypes = {
  freightLanePricesData: PropTypes.object,
  apiRef: PropTypes.object.isRequired,
  isUpdate: PropTypes.bool,
  setHasEditedPrices: PropTypes.func,
}

FreightLaneBladePricing.defaultProps = {
  freightLanePricesData: null,
  isUpdate: false,
  setHasEditedPrices: () => {},
}

export default FreightLaneBladePricing
